import type {ReactElement, ReactNode} from 'react';
import React, {useEffect, useRef, useState} from 'react';
import type {DROPDOWN_SIZE} from '@Components/dropdown-v2/dropdown.types';
import {ALIGNMENT_TYPE, DROPDOWN_POSITION} from '@Components/dropdown-v2/dropdown.types';
import {Option} from '@Components/contextual-menu/components/option';
import {Dropdown} from '@Components/dropdown-v2';
import {ControlledList} from '@Components/controlled-list';
import type {ControlledListItemType} from '@Components/controlled-list/controlled-list.types';
import {CONTROLLED_LIST_ITEM_TYPE} from '@Components/controlled-list/controlled-list.types';
import {DropdownGhostSelectBox} from '@Components/dropdown-v2/components/dropdown-ghost-select-box';
import {DropdownDefaultSelectBox} from '@Components/dropdown-v2/components/dropdown-default-select-box';
import styles from './contextual-menu-dropdown-option.module.scss';

export type DropdownIdType = string | number;

export interface ContextualMenuDropdownOptionProps {
  id: string;
  className?: string;
  label: string;
  dropdownItems?: Array<DropdownItemStorage>;
  selectedItemId?: DropdownIdType;
  selectorIcon?: string;
  selectorSvg?: ReactElement<any>;
  selectorSize?: DROPDOWN_SIZE;
  selectorSvgClassName?: string;
  /**
   * this prop gets preference over dropdownSelectorText
   */
  dropdownSelectorText?: string;
  alignment?: ALIGNMENT_TYPE;
  /**
   * helps in case you want to send something custom in the dropdown popup
   */
  customPopup?: ReactElement<any>;
  hasDefaultSpacing?: boolean;
  /**
   * this prop gets preference over the default ghost dropdown selector type
   */
  customSelector?: ReactElement<any>;
  /**
   * this is by default 254px
   */
  popupWidth?: number;
  /**
   * this is by default 360px
   */
  popupMaxHeight?: number;
  children?: ReactNode;
  position?: DROPDOWN_POSITION;
  selectorType?: ContextualMenuDropdownType;

  /**
   * max-height for the dropdown container that holds the popup
   */
  maxHeight?: number;

  /**
   * not supported for custom selectors
   */
  isLoading?: boolean;
  isDisabled?: boolean;
  leftOffset?: number;
  isForSubOption?: boolean;
  selectorClasses?: string;
  popUpHasCustomHeight?: boolean;
  popUpHasCustomWidth?: boolean;
  doesSubOptionHaveTransition?: boolean;
  showModalForSmallScreen?: boolean;
  modalTitle?: string;

  isSmallScreenModalBottomSheet?: boolean;
  isBottomSheetNonBlocking?: boolean;

  onClick?(itemId: DropdownIdType): void;

  onOpen?(): void;
  onClose?(): void;
}

export enum ContextualMenuDropdownType {
  GHOST = 'ghost',
  DEFAULT = 'default',
}

export interface DropdownItemStorage {
  text: string;
  id: DropdownIdType;
}

export function ContextualMenuDropdownOption({
  className = '',
  customPopup = undefined,
  hasDefaultSpacing = false,
  customSelector = undefined,
  dropdownSelectorText = '',
  dropdownItems = [],
  selectedItemId = '',
  alignment = ALIGNMENT_TYPE.BOTTOM_END,
  popupWidth = 254,
  popupMaxHeight = 360,
  children = null,
  position = DROPDOWN_POSITION.ABSOLUTE,
  selectorType = ContextualMenuDropdownType.GHOST,
  isLoading = false,
  isDisabled = false,
  onClick = (): void => {},
  onOpen = (): void => {},
  onClose = (): void => {},
  leftOffset = 0,
  isForSubOption = false,
  selectorClasses = '',
  selectorIcon = '',
  selectorSvg = undefined,
  popUpHasCustomWidth = true,
  popUpHasCustomHeight = true,
  doesSubOptionHaveTransition = true,
  showModalForSmallScreen = true,
  isSmallScreenModalBottomSheet = false,
  isBottomSheetNonBlocking = false,
  modalTitle = '',
  ...props
}: ContextualMenuDropdownOptionProps): ReactElement<any> | null {
  const [selectorText, setSelectorText] = useState('');
  const areItemIdsString = useRef(true);

  useEffect(() => {
    dropdownItems?.forEach((item) => {
      if (String(item.id) === String(selectedItemId)) {
        setSelectorText(item.text);
      }
    });
  }, [selectedItemId]);

  const getDropdownPopup = (): ReactElement<any> => {
    if (customPopup) {
      return customPopup;
    }
    const items: ControlledListItemType[] = dropdownItems.map((name) => {
      if (typeof name.id === 'number') {
        areItemIdsString.current = false;
      }
      return {
        id: String(name.id),
        text: name.text,
        type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_3,
        isSelected: String(name.id) === String(selectedItemId),
        icon: 'icon-media',
      };
    });
    return (
      <div style={{width: popUpHasCustomWidth ? `${popupWidth}px` : '100%', maxHeight: `${popupMaxHeight}px`}}>
        <ControlledList
          className="spacing-m-0"
          id="unqiue-list-id"
          items={items}
          onClick={(listItemId): void => {
            if (listItemId !== selectedItemId) {
              onClick(areItemIdsString.current ? listItemId : Number(listItemId));
            }
          }}
        />
      </div>
    );
  };

  const getDropdownSelector = (): ReactElement<any> => {
    if (customSelector) {
      return customSelector;
    }

    switch (selectorType) {
      case ContextualMenuDropdownType.GHOST:
        return (
          <DropdownGhostSelectBox
            icon={selectorIcon}
            svg={selectorSvg}
            className={`${isForSubOption ? styles.transparentBorder : ''} ${selectorClasses}`}
            isLoading={isLoading}
            isDisabled={isDisabled}
            text={dropdownSelectorText || selectorText}
            size={props.selectorSize}
            selectorSvgClassName={props.selectorSvgClassName}
          />
        );
      case ContextualMenuDropdownType.DEFAULT:
        return (
          <DropdownDefaultSelectBox isDisabled={isDisabled} icon={selectorIcon} className={selectorClasses} isLoading={isLoading} text={dropdownSelectorText || selectorText} />
        );
      default:
        throw new Error('Unhandled dropdown selector type');
    }
  };
  return (
    <Option
      key={props.id}
      label={props.label}
      className={className}
      doesSuboptionHaveTransition={doesSubOptionHaveTransition}
      optionControl={
        <Dropdown
          isSmallScreenModalBottomSheet={isSmallScreenModalBottomSheet}
          isBottomSheetNonBlocking={isBottomSheetNonBlocking}
          popupSpacingClass={`${hasDefaultSpacing ? 'spacing-p-2' : 'spacing-p-t-2 spacing-p-r-2 spacing-p-l-2'} `}
          position={position}
          alignment={alignment}
          popUpHasCustomWidth={popUpHasCustomWidth}
          popUpHasCustomHeight={popUpHasCustomHeight}
          selector={getDropdownSelector()}
          popup={getDropdownPopup()}
          leftOffset={leftOffset}
          onOpen={onOpen}
          popupClassName="border-s-standard"
          showModalForSmallScreen={showModalForSmallScreen}
          modalTitle={modalTitle}
          onClose={onClose}
          maxHeight={props.maxHeight}
        />
      }
    >
      {children}
    </Option>
  );
}
